import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBackRounded,
  SaveRounded,
  MenuBookRounded as MenuBookIcon,
} from '@mui/icons-material';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { useAcademic } from '../../hooks/use-academic';
import { useCanManageAcademics } from '../../hooks/use-can-manage-academics';
import { detailEditable } from './academic-structure.actions';
import { RepeatableListEditor, type RepeatableListItem } from './components/RepeatableListEditor';

// Workaround for MUI's exactOptionalPropertyTypes incompatibility in Autocomplete renderInput.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autocompleteField = (params: any) => params as React.ComponentProps<typeof TextField>;

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 1.2,
          mb: 2,
          display: 'block',
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CourseDetailPage() {
  const { t } = useTranslation('sis');
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const { updateCourse, isUpdatingCourse } = useAcademic();
  // Quien no gestiona CONSULTA la ficha del curso (2a-1b): sin Guardar y con
  // los campos bloqueados. updateCourse ya exigía SECTION_MANAGEMENT_ROLES.
  const editable = detailEditable({ canManage: useCanManageAcademics() });

  // Identity
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [credits, setCredits] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'archived'>('active');
  const [programId, setProgramId] = useState<string>('');
  const [programs, setPrograms] = useState<{ id: string; name: string; code: string }[]>([]);
  const [prerequisites, setPrerequisites] = useState<string[]>([]);
  const [courses, setCourses] = useState<{ id: string; name: string; code: string }[]>([]);

  // Academic identity (Phase C — Course is source of truth)
  const [description, setDescription] = useState('');
  const [learningObjectives, setLearningObjectives] = useState<RepeatableListItem[]>([]);

  // Anchor verse + biblical version
  const [anchorReference, setAnchorReference] = useState('');
  const [anchorText, setAnchorText] = useState('');
  const [anchorTranslation, setAnchorTranslation] = useState('');
  const [biblicalVersion, setBiblicalVersion] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [courseName, setCourseName] = useState('');

  // Load course via realtime snapshot
  useEffect(() => {
    if (!currentTenantId || !courseId) return;
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, `tenants/${currentTenantId}/courses/${courseId}`),
      (snap) => {
        setLoading(false);
        if (!snap.exists()) {
          setError(t('courseDetail.notFound'));
          return;
        }
        const d = snap.data();
        setCourseName(d['name'] || '');
        setName(d['name'] || '');
        setCode(d['code'] || '');
        setCredits(d['credits'] ?? '');
        setStatus(d['status'] || 'active');
        setProgramId((d['programId'] as string | undefined) ?? '');
        setDescription((d['description'] as string | undefined) ?? '');
        setLearningObjectives(
          (
            (d['learningObjectives'] as
              | { id: string; code?: string; text: string }[]
              | undefined) ?? []
          ).map((o) => ({
            id: o.id,
            ...(o.code ? { code: o.code } : {}),
            text: o.text,
          })),
        );
        const verse = d['anchorVerse'] as
          | { reference?: string; text?: string; translation?: string }
          | undefined;
        setAnchorReference(verse?.reference ?? '');
        setAnchorText(verse?.text ?? '');
        setAnchorTranslation(verse?.translation ?? '');
        setBiblicalVersion((d['biblicalVersion'] as string | undefined) ?? '');
        setPrerequisites((d['prerequisites'] as string[] | undefined) ?? []);
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
    return unsub;
  }, [currentTenantId, courseId, t]);

  // Load active programs for the selector.
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      setPrograms(
        snap.docs
          .filter((d) => (((d.data() as any)['status'] as string) ?? 'active') === 'active')
          .map((d) => ({
            id: d.id,
            name: (d.data()['name'] as string) ?? d.id,
            code: (d.data()['code'] as string) ?? '',
          })),
      );
    });
    return () => unsub();
  }, [currentTenantId]);

  // Load courses for the prerequisite picker (exclude self at render time).
  useEffect(() => {
    if (!currentTenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (snap) => {
      setCourses(
        snap.docs
          .filter((d) => !(d.data() as { deletedAt?: unknown })['deletedAt'])
          .map((d) => ({
            id: d.id,
            name: (d.data()['name'] as string) ?? d.id,
            code: (d.data()['code'] as string) ?? '',
          })),
      );
    });
    return () => unsub();
  }, [currentTenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editable || !currentTenantId || !courseId) return;
    setError(null);
    setSuccess(false);
    try {
      const cleanObjectives = learningObjectives
        .filter((o) => o.text.trim())
        .map((o) => ({
          id: o.id,
          ...(o.code && o.code.trim() ? { code: o.code.trim() } : {}),
          text: o.text.trim(),
        }));

      // anchorVerse: send object when all three filled, null to clear, otherwise omit
      const anyAnchor = anchorReference.trim() || anchorText.trim() || anchorTranslation.trim();
      const allAnchor = anchorReference.trim() && anchorText.trim() && anchorTranslation.trim();

      const payload: Parameters<typeof updateCourse>[0] = {
        tenantId: currentTenantId,
        courseId,
        name,
        code,
        credits: Number(credits) || 0,
        status: status === 'archived' ? 'inactive' : status,
        description,
        learningObjectives: cleanObjectives,
        biblicalVersion: biblicalVersion.trim(),
        programId: programId || null,
        prerequisites,
      };
      if (allAnchor) {
        payload.anchorVerse = {
          reference: anchorReference.trim(),
          text: anchorText.trim(),
          translation: anchorTranslation.trim(),
        };
      } else if (!anyAnchor) {
        payload.anchorVerse = null;
      }
      // If only some anchor fields filled — surface validation error
      if (anyAnchor && !allAnchor) {
        setError(t('courseDetail.anchorIncomplete'));
        return;
      }

      await updateCourse(payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      const msg = String(err?.message ?? '');
      // Backend rejects a prerequisite cycle (A→B→A) — surface a translated hint.
      if (msg.toLowerCase().includes('cycle')) {
        setError(t('courseDetail.prerequisites.cycleError'));
      } else {
        setError(msg || t('courseDetail.saveError'));
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={12}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* ── Sticky header ─────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
          mb: 4,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate('/academic')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {t('courseDetail.backToCourses')}
            </Button>
            <Typography color="text.disabled">/</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MenuBookIcon sx={{ fontSize: 18, color: '#6366f1' }} />
              <Typography variant="h6" fontWeight={700}>
                {courseName || t('courseDetail.courseFallback')}
              </Typography>
            </Stack>
          </Stack>
          {editable && (
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveRounded />}
              disabled={isUpdatingCourse}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              {isUpdatingCourse ? t('courseDetail.actions.saving') : t('courseDetail.actions.save')}
            </Button>
          )}
        </Stack>

        {!editable && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('courseDetail.readOnly')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('courseDetail.updateSuccess')}
          </Alert>
        )}
      </Box>

      {/* ── Content ────────────────────────────────────────────────── */}
      {/* Solo lectura: el fieldset deshabilitado bloquea los campos nativos. */}
      <Box
        component="fieldset"
        disabled={!editable}
        sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
        px={3}
        pb={6}
      >
        <Stack spacing={3}>
          {/* — Información general — */}
          <Section title={t('courseDetail.sections.general')}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('courseDetail.fields.name')}
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('courseDetail.fields.code')}
                  required
                  fullWidth
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('courseDetail.fields.credits')}
                  type="number"
                  required
                  fullWidth
                  value={credits}
                  onChange={(e) => setCredits(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t('courseDetail.fields.status')}
                  required
                  fullWidth
                  value={status === 'archived' ? 'inactive' : status}
                  onChange={(e) => setStatus(e.target.value as any)}
                >
                  <MenuItem value="active">{t('courseDetail.statusOptions.active')}</MenuItem>
                  <MenuItem value="inactive">{t('courseDetail.statusOptions.inactive')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label={t('courseDetail.fields.program')}
                  fullWidth
                  value={programId}
                  onChange={(e) => setProgramId(e.target.value)}
                  helperText={t('courseDetail.fields.programHelper')}
                >
                  <MenuItem value="">
                    <em>{t('courseDetail.noProgram')}</em>
                  </MenuItem>
                  {programs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.code ? `${p.code} — ` : ''}
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Section>

          {/* — Identidad académica — */}
          <Section title={t('courseDetail.sections.academic')}>
            <Stack spacing={3}>
              <TextField
                label={t('courseDetail.fields.description')}
                fullWidth
                multiline
                minRows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                helperText={t('courseDetail.fields.descriptionHelper')}
              />
              <RepeatableListEditor
                label={t('courseDetail.oacs.label')}
                helperText={t('courseDetail.oacs.helper')}
                showCode
                textLabel={t('courseDetail.oacs.textLabel')}
                textPlaceholder={t('courseDetail.oacs.textPlaceholder')}
                items={learningObjectives}
                onChange={setLearningObjectives}
                autoCodePrefix="OAC"
              />
            </Stack>
          </Section>

          {/* — Prerequisitos — */}
          <Section title={t('courseDetail.prerequisites.title')}>
            <Stack spacing={1}>
              <Autocomplete
                multiple
                options={courses.filter((c) => c.id !== courseId)}
                getOptionLabel={(o) => (o.code ? `${o.code} — ${o.name}` : o.name)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={courses.filter((c) => prerequisites.includes(c.id))}
                onChange={(_e, value) => setPrerequisites(value.map((c) => c.id))}
                renderInput={(params) => (
                  <TextField
                    {...autocompleteField(params)}
                    label={t('courseDetail.prerequisites.label')}
                    placeholder={t('courseDetail.prerequisites.placeholder')}
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary">
                {t('courseDetail.prerequisites.helper')}
              </Typography>
            </Stack>
          </Section>

          {/* — Identidad bíblica — */}
          <Section title={t('courseDetail.sections.biblical')}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('courseDetail.fields.anchorReference')}
                  fullWidth
                  placeholder={t('courseDetail.fields.anchorReferencePlaceholder')}
                  value={anchorReference}
                  onChange={(e) => setAnchorReference(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label={t('courseDetail.fields.anchorText')}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={t('courseDetail.fields.anchorTextPlaceholder')}
                  value={anchorText}
                  onChange={(e) => setAnchorText(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('courseDetail.fields.anchorTranslation')}
                  fullWidth
                  placeholder={t('courseDetail.fields.anchorTranslationPlaceholder')}
                  value={anchorTranslation}
                  onChange={(e) => setAnchorTranslation(e.target.value)}
                  helperText={t('courseDetail.fields.anchorTranslationHelper')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('courseDetail.fields.biblicalVersion')}
                  fullWidth
                  placeholder={t('courseDetail.fields.biblicalVersionPlaceholder')}
                  value={biblicalVersion}
                  onChange={(e) => setBiblicalVersion(e.target.value)}
                  helperText={t('courseDetail.fields.biblicalVersionHelper')}
                />
              </Grid>
            </Grid>
          </Section>
        </Stack>
      </Box>
    </form>
  );
}
